/* eslint-disable max-len */
import { useEffect } from 'react';
import proptypes from 'prop-types';
import {
  arrayOf as arrayType,
  bool as boolType,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import {
  useHelmet, TitleTag, MetaTag, ScriptTag, LinkTag
} from '@thd-nucleus/thd-helmet';
import { copy } from '@thd-olt-functional/utils';
import {
  formatDescription,
  formatProductLabel,
  getDynamicOGTags,
  getHoistedImgPath,
  getPrimaryImage,
  getSecondaryImgPath
} from '../helpers/product-helpers';
import { getVideoStructuredData } from './structured-data/getVideoStructuredData';
import { getBreadcrumbStructuredData } from './structured-data/getBreadcrumbStructuredData';
import { getProductStructuredData } from './structured-data/getProductStructuredData';
import { DEFAULT_REVIEWS_SORT_BY } from '../constants';

const { bool, string } = proptypes;

const transformBrandName = (brandName) => {
  // eslint-disable-next-line
  if (brandName == undefined || brandName?.toLowerCase() === 'unbranded') {
    return '';
  }
  return brandName;
};

const removeDoubleSpaces = (sentence) => {
  return sentence.replace(/\s{2,}/g, ' ');
};

const generatePprTitle = ({
  brandName,
  identifiers = {},
  props = {},
  seoPageNumber,
  productDescription
}) => {
  let pageTitle;
  const defaultTitle = `${brandName} ${identifiers?.productLabel || ''} ${identifiers?.modelNumber || ''} ${props?.pageNumber ? '| Pg' + props.pageNumber : ''}`.trim();
  if (props?.ppq) {
    pageTitle = removeDoubleSpaces(
      `Have a question about ${productDescription}? - Pg ${seoPageNumber} - The Home Depot`);
  } else if (props?.pageNumber) {
    pageTitle = removeDoubleSpaces(
      `Reviews for ${productDescription} | Pg ${seoPageNumber} - The Home Depot`
    );
  }

  return pageTitle?.trim() || defaultTitle;
};

const generatePprDescription = ({
  seoDescription,
  details = {},
  identifiers = {},
  props = {},
  seoPageNumber,
  productDescription
}) => {
  let pageDescription;
  const defaultDescription = formatDescription(seoDescription || details?.description);
  if (props?.ppq) {
    pageDescription = removeDoubleSpaces(
      `See what other customers have asked about ${productDescription} ${identifiers?.modelNumber} on Page ${seoPageNumber}.`);
  } else if (props?.pageNumber) {
    pageDescription = removeDoubleSpaces(
      `Read page ${seoPageNumber} of our customer reviews for more information on the ${productDescription}.`
    );
  }

  return pageDescription || defaultDescription;
};

const generatePprEncodedPath = ({ identifiers, props }) => {
  const { canonicalUrl: canonicalPath = '' } = identifiers || {};
  let identifierCanonicalUrl = canonicalPath;
  if (props?.ppq) {
    identifierCanonicalUrl = canonicalPath?.replace('/p/', '/p/questions/');
  } else {
    identifierCanonicalUrl = canonicalPath?.replace('/p/', '/p/reviews/');
  }

  const resultedPath = props?.pageNumber ? identifierCanonicalUrl?.concat(`/${props.pageNumber}`) : identifierCanonicalUrl;
  return encodeURI(resultedPath);
};

const getAggregateReviews = (data) => {
  const aggregateReviews = {};

  if (data?.reviews?.Includes?.Products?.store?.FilteredReviewStatistics?.AverageOverallRating
    && data?.reviews?.TotalResults) {
    aggregateReviews.averageRating = data.reviews.Includes.Products.store.FilteredReviewStatistics.AverageOverallRating;
    aggregateReviews.totalReviews = data.reviews.TotalResults;
  }
  return aggregateReviews;
};

const pprMetaDataHandler = ({ data, props = {} }) => {
  if (!data?.product) {
    return [];
  }

  const {
    itemId,
    details,
    identifiers,
    seo,
    availabilityType,
    media,
    taxonomy,
    pricing,
    specificationGroup
  } = data?.product || {};

  const transformedBrandName = transformBrandName(identifiers?.brandName);
  const productDescription = (`${transformedBrandName} ${identifiers?.productLabel}`);

  const seoPageNumber = parseInt(props?.pageNumber, 10);
  let pageDescription;

  if (props?.ppq) {
    pageDescription = removeDoubleSpaces(
      `See what other customers have asked about ${productDescription} ${identifiers?.modelNumber} on Page ${seoPageNumber}.`);
  } else if (props?.pageNumber) {
    pageDescription = removeDoubleSpaces(
      `Read page ${seoPageNumber} of our customer reviews for more information on the ${productDescription}.`
    );
  }

  const productLabel = formatProductLabel({ identifiers });
  const brandName = (!identifiers?.brandName || identifiers?.brandName?.toLowerCase() === 'unbranded') ? '' : identifiers.brandName;

  const title = generatePprTitle({
    brandName,
    identifiers,
    props,
    seoPageNumber,
    productDescription
  });

  const { seoDescription } = seo || {};
  const description = generatePprDescription({
    seoDescription,
    details,
    identifiers,
    props,
    seoPageNumber,
    productDescription
  });

  const encodedPath = generatePprEncodedPath({ identifiers, props });

  const canonical = `https://www.homedepot.com${encodedPath}`;
  const { video } = media || {};
  const primaryImage = getPrimaryImage({ media });
  const alternate = `android-app://com.thehomedepot/homedepot/product/${itemId}`;
  const hoistedImgPath = getHoistedImgPath({ primaryImage });
  const secondaryImgPath = getSecondaryImgPath({ media });
  const MAX_PAGE_NUMBER_TO_INDEX = 5;

  const dynamicOGTags = getDynamicOGTags({
    props, brandName, identifiers, title, description
  });

  const tags = [
    new TitleTag(title),
    new MetaTag({ name: 'description', content: description, id: 'description' }),
    ...dynamicOGTags,
    new MetaTag({ property: 'og:url', content: canonical, id: 'ogUrl' }),
    new MetaTag({ property: 'og:image', content: primaryImage, id: 'ogImage' }),
    new MetaTag({ property: 'og:site_name', content: 'The Home Depot', id: 'ogSiteName' }),
    new MetaTag({ property: 'fb:admins', content: '100005416960152', id: 'fbAdmins' }),
    new LinkTag({ rel: 'canonical', href: canonical, id: 'canonical' }),
    new LinkTag({ rel: 'alternate', href: alternate, id: 'alternate' }),
    new LinkTag({
      rel: 'preload', href: hoistedImgPath, id: 'preloadImg', as: 'image', media: '(max-width: 600px)', fetchpriority: 'high'
    }),
  ];
  if (secondaryImgPath) {
    tags.push(new LinkTag({
      rel: 'preload', href: secondaryImgPath, id: 'preloadSecondaryImg', as: 'image', media: '(max-width: 600px)', fetchpriority: 'high'
    }));
  }

  const aggregateReviews = getAggregateReviews(data);

  const productStructure = getProductStructuredData({
    aggregateReviews,
    reviews: data?.reviews?.Results,
    identifiers: {
      ...identifiers,
      productLabel
    },
    media,
    details,
    pricing,
    specifications: specificationGroup,
    availabilityType,
    ppq: props.ppq
  });

  tags.push(new ScriptTag({ content: productStructure, id: 'productStructureData' }));

  const breadcrumbStructuredData = getBreadcrumbStructuredData({
    canonical,
    breadcrumbs: copy(taxonomy?.breadCrumbs || []).data,
    title
  });

  tags.push(new ScriptTag({ content: breadcrumbStructuredData, id: 'breadcrumbStructureData' }));

  if (availabilityType?.discontinued || props?.pageNumber > MAX_PAGE_NUMBER_TO_INDEX) {
    tags.push(new MetaTag({ name: 'ROBOTS', content: 'NOINDEX, NOFOLLOW', id: 'robots' }));
  } else {
    tags.push(new MetaTag({ name: 'robots', content: 'max-image-preview:large', id: 'robots' }));
  }

  if (video?.length) {
    const videoStructuredData = getVideoStructuredData({
      videos: video,
      productLabel,
    });
    tags.push(new ScriptTag({ content: videoStructuredData, id: 'videoStructureData' }));
  }

  return tags;
};

const PprMetadata = ({
  itemId, page = '1', ppq = false
}) => {
  const pageSize = 30;

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-metadata.ready'); }, []);
  const productOptions = {
    variables: {
      itemId
    }
  };

  const pageNumber = page || '1';
  const startIndex = pageNumber === '1' ? 1 : (pageNumber - 1) * pageSize + 1;

  const reviewsOptions = {
    variables: {
      itemId,
      ...(pageNumber && {
        pagesize: `${pageSize}`,
        recfirstpage: `${pageSize}`,
        sortBy: DEFAULT_REVIEWS_SORT_BY
      }),
      ...(!ppq && { startIndex })
    },
  };

  const { data, loading, error, variables } = useDataModel('product', productOptions);
  const { data: reviewData, loading: reviewLoading, error: reviewError } = useDataModel('reviews', reviewsOptions);

  const props = {
    ppq,
    pageNumber,
  };

  const helmetData = {
    product: data?.product,
    reviews: reviewData?.reviews
  };
  const deps = data?.product && reviewData?.reviews && data?.product?.itemId;
  useHelmet('pprMetadata', { data: helmetData, props, variables }, pprMetaDataHandler, [deps]);
  return null;

};

PprMetadata.displayName = 'PprMetadata';

PprMetadata.dataModel = {
  product: params({ itemId: stringType().isRequired() }).shape({
    itemId: stringType(),
    dataSources: stringType(),
    availabilityType: shapeType({
      discontinued: boolType(),
      status: boolType()
    }),
    details: shapeType({
      description: stringType()
    }),
    identifiers: shapeType({
      brandName: stringType(),
      canonicalUrl: stringType(),
      itemId: stringType(),
      modelNumber: stringType(),
      productLabel: stringType(),
      storeSkuNumber: stringType(),
      upcGtin13: stringType()
    }),
    media: shapeType({
      image: shapeType({
        url: stringType()
      }).client(),
      images: arrayType(shapeType({
        url: stringType(),
        type: stringType(),
        subType: stringType(),
        sizes: arrayType(stringType())
      })),
      video: arrayType(shapeType({
        shortDescription: stringType(),
        thumbnail: stringType(),
        url: stringType(),
        videoStill: stringType(),
        uploadDate: stringType(),
        dateModified: stringType(),
      }))
    }),
    pricing: params({ storeId: stringType() }).shape({
      promotion: shapeType({
        dates: shapeType({
          end: stringType()
        })
      }),
      value: numberType({ float: true }),
      original: numberType({ float: true })
    }),
    reviews: shapeType({
      ratingsReviews: shapeType({
        averageRating: stringType(),
        totalReviews: stringType()
      })
    }),
    seo: shapeType({
      seoKeywords: stringType(),
      seoDescription: stringType()
    }),
    specificationGroup: arrayType(shapeType({
      specifications: arrayType(shapeType({
        specName: stringType(),
        specValue: stringType()
      })),
      specTitle: stringType()
    })),
    taxonomy: shapeType({
      breadCrumbs: arrayType(shapeType({
        label: stringType(),
        url: stringType()
      }))
    })
  }),
  reviews: params({ itemId: stringType().isRequired() }).shape({
    Results: arrayType(shapeType({
      Rating: numberType(),
      ReviewText: stringType(),
      Title: stringType(),
      UserNickname: stringType()
    })),
    Includes: shapeType({
      Products: shapeType({
        store: shapeType({
          FilteredReviewStatistics: shapeType({
            AverageOverallRating: stringType()
          })
        })
      })
    }),
    TotalResults: numberType()
  })
};

PprMetadata.propTypes = {
  itemId: string.isRequired,
  page: string,
  ppq: bool,
};

PprMetadata.defaultProps = {};

export { PprMetadata };
